// src/components/MaintenancePage.jsx

import React, { useState, useEffect, useRef } from 'react';
import './MaintenancePage.css';

/* ── Countdown Block ─────────────────────────────────────── */
const Block = ({ value, label, prev }) => {
    const [flip, setFlip] = useState(false);

    useEffect(() => {
        if (value !== prev) {
            setFlip(true);
            const t = setTimeout(() => setFlip(false), 420);
            return () => clearTimeout(t);
        }
    }, [value, prev]);

    const pad = (n) => String(n).padStart(2, '0');

    return (
        <div className="mp-block">
            <div className={`mp-card ${flip ? 'mp-card--flip' : ''}`}>
                <span className="mp-digits">{pad(value)}</span>
            </div>
            <span className="mp-label">{label}</span>
        </div>
    );
};

/* ── Countdown Logic ─────────────────────────────────────── */
const useCountdown = (targetDate) => {
    const calcDiff = () => Math.max(0, +new Date(targetDate) - Date.now());

    const [diff, setDiff] = useState(calcDiff);
    const prevRef = useRef({ d: 0, h: 0, m: 0, s: 0 });

    useEffect(() => {
        const tick = () => setDiff(calcDiff());
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [targetDate]);

    useEffect(() => {
        if (diff === 0) {
            const t = setTimeout(() => window.location.reload(), 2000);
            return () => clearTimeout(t);
        }
    }, [diff]);

    const days    = Math.floor(diff / 86400000);
    const hours   = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000)  / 60000);
    const seconds = Math.floor((diff % 60000)    / 1000);

    const prev = { ...prevRef.current };
    prevRef.current = { d: days, h: hours, m: minutes, s: seconds };

    return { days, hours, minutes, seconds, prev, expired: diff === 0 };
};

/* ── Animated Dots Background ────────────────────────────── */
const DotsBackground = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let animId;

        const resize = () => {
            canvas.width  = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const COLS = 30;
        const dots = Array.from({ length: COLS * COLS }, (_, i) => ({
            x: (i % COLS) / (COLS - 1),
            y: Math.floor(i / COLS) / (COLS - 1),
            phase: Math.random() * Math.PI * 2,
            speed: 0.4 + Math.random() * 0.6,
        }));

        let t = 0;
        const draw = () => {
            t += 0.012;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            dots.forEach(dot => {
                const alpha = 0.06 + 0.09 * Math.sin(t * dot.speed + dot.phase);
                const r = 1.2 + 0.8 * Math.sin(t * dot.speed * 0.7 + dot.phase);
                ctx.beginPath();
                ctx.arc(dot.x * canvas.width, dot.y * canvas.height, r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255, 180, 50, ${alpha})`;
                ctx.fill();
            });
            animId = requestAnimationFrame(draw);
        };
        draw();

        return () => {
            cancelAnimationFrame(animId);
            window.removeEventListener('resize', resize);
        };
    }, []);

    return <canvas ref={canvasRef} className="mp-canvas" aria-hidden="true" />;
};

/* ── Main Component ─────────────────────────────────────── */
export default function MaintenancePage({ endDate, nomeEmpresa }) {
    const hasDate = !!endDate;
    const { days, hours, minutes, seconds, prev, expired } = useCountdown(
        hasDate ? endDate : new Date(Date.now() + 999999999)
    );

    const fmtDate = (d) => new Date(d).toLocaleString('pt-BR', {
        weekday: 'long', day: 'numeric', month: 'long',
        hour: '2-digit', minute: '2-digit'
    });

    return (
        <div className="mp-root">
            <DotsBackground />

            {/* Ambient ring */}
            <div className="mp-ring mp-ring--1" aria-hidden="true" />
            <div className="mp-ring mp-ring--2" aria-hidden="true" />

            <div className="mp-content">
                {/* Logo / Brand */}
                <div className="mp-brand">
                    <div className="mp-logo-wrap">
                        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
                            <rect width="36" height="36" rx="10" fill="#FF8C00" />
                            <path d="M10 26l4-8 4 5 3-4 5 7H10z" fill="white" opacity=".9"/>
                            <circle cx="24" cy="13" r="3" fill="white" opacity=".7"/>
                        </svg>
                    </div>
                    <span className="mp-brand-name">{nomeEmpresa || 'Sistema'}</span>
                </div>

                {/* Icon */}
                <div className="mp-icon-wrap" aria-hidden="true">
                    <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
                        <circle cx="26" cy="26" r="26" fill="rgba(255,140,0,0.12)" />
                        <path d="M33.5 14.5c-1.5-.6-3.1-.5-4.5.2L32 18l-2 4-4 2-3.5-3c-.7 1.4-.8 3-.2 4.5 1 2.5 3.6 4 6.2 3.7l6.5 6.5a2.5 2.5 0 003.5-3.5L32 25.7c.4-2.6-1.1-5.2-3.5-6.2h5z" fill="#FF8C00" stroke="#FF8C00" strokeWidth=".5" strokeLinejoin="round"/>
                        <path d="M16 32l7-7" stroke="#FF8C00" strokeWidth="2.5" strokeLinecap="round"/>
                        <circle cx="14" cy="34" r="2.5" fill="#FF8C00" opacity=".6"/>
                    </svg>
                </div>

                {/* Headline */}
                <h1 className="mp-title">Sistema em Manutenção</h1>
                <p className="mp-subtitle">
                    Estamos trabalhando para melhorar sua experiência.<br />
                    O sistema voltará em breve — agradecemos sua paciência.
                </p>

                {/* Countdown */}
                {hasDate && !expired && (
                    <div className="mp-countdown-wrap">
                        <p className="mp-countdown-label">
                            Previsão de retorno
                            <span className="mp-countdown-date">{fmtDate(endDate)}</span>
                        </p>
                        <div className="mp-countdown">
                            {days > 0 && (
                                <Block value={days} label="dias" prev={prev.d} />
                            )}
                            <Block value={hours}   label="horas"    prev={prev.h} />
                            <Block value={minutes} label="minutos"  prev={prev.m} />
                            <Block value={seconds} label="segundos" prev={prev.s} />
                        </div>
                    </div>
                )}

                {expired && (
                    <div className="mp-ready">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <circle cx="10" cy="10" r="10" fill="#22c55e" opacity=".15"/>
                            <path d="M5.5 10.5l3 3 6-6" stroke="#22c55e" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Retornando ao sistema…
                    </div>
                )}

                {/* Footer */}
                <footer className="mp-footer">
                    Precisa de suporte urgente?&nbsp;
                    <a href="mailto:suporte@sistema.com.br" className="mp-link">
                        Entre em contato
                    </a>
                </footer>
            </div>
        </div>
    );
}